import React, { useState, useEffect } from 'react';
import { Camera } from 'lucide-react';
import ClassCard from '../ClassCard/ClassCard';
import CompetitionGrid from '../Home/CompetitionGrid';
import styles from './createClasses.module.css';
import withEditRights from '../hoc/with-edit-rights';
import { getComponentTranslator } from '../../util/i18n';
import apiClient from '../../util/api-client';

const translate = getComponentTranslator('classes');

function CreateClassesPage({ canEdit }) {
  const [classes, setClasses] = useState(() => {
    try {
      const saved = localStorage.getItem('classes');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading classes from localStorage:', error);
      return [];
    }
  });

  // Hämta den valda klassen från localStorage (om den finns)
  const [selectedClass, setSelectedClass] = useState(() => {
    try {
      const savedSelectedClass = localStorage.getItem('selectedClass');
      return savedSelectedClass ? JSON.parse(savedSelectedClass) : null;
    } catch (error) {
      console.error('Error loading selected class from localStorage:', error);
      return null;
    }
  });

  const [newClass, setNewClass] = useState({ name: '', image: '' });
  const [classTotals, setClassTotals] = useState([]);

  const [competitions, setCompetitions] = useState(() => {
    try {
      const savedCompetitions = localStorage.getItem('competitions');
      return savedCompetitions ? JSON.parse(savedCompetitions) : [];
    } catch (error) {
      console.error('Error loading competitions from localStorage:', error);
      return [];
    }
  });

  // Fetch initial classes and competitions from MongoDB backend
  useEffect(() => {
    apiClient.getClasses().then((data) => {
      if (Array.isArray(data)) setClasses(data);
    });
    apiClient.getCompetitions().then((data) => {
      if (Array.isArray(data)) setCompetitions(data);
    });
  }, []);

  // Spara klasser i MongoDB och localStorage när de uppdateras
  useEffect(() => {
    try {
      if (Array.isArray(classes)) {
        apiClient.saveClasses(classes);
      }
    } catch (error) {
      console.error('Error saving classes to backend:', error);
    }
  }, [classes]);

  // Spara den valda klassen i localStorage när den uppdateras
  useEffect(() => {
    try {
      if (selectedClass) {
        // Spara bara nödvändig information för den valda klassen
        const minimalSelectedClass = {
          id: selectedClass.id || selectedClass._id || selectedClass.name,
          name: selectedClass.name
        };
        localStorage.setItem('selectedClass', JSON.stringify(minimalSelectedClass));
      } else {
        localStorage.removeItem('selectedClass');
      }
    } catch (error) {
      console.error('Error saving selected class to localStorage:', error);
    }
  }, [selectedClass]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Komprimera bilden innan den sparas
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 400;
          const MAX_HEIGHT = 400;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedImage = canvas.toDataURL('image/jpeg', 0.6);
          setNewClass((prev) => ({ ...prev, image: compressedImage }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const openFileDialog = () => {
    document.getElementById('image-upload').click();
  };

  const createNewClass = (e) => {
    e.preventDefault();
    if (!newClass.name || !newClass.name.trim()) return;
    const newClassObj = {
      id: Date.now().toString(),
      name: newClass.name.trim(),
      image: newClass.image || '',
      score: 0,
      scores: [],
      competitions: [],
    };
    const updated = [...classes, newClassObj];
    setClasses(updated);
    apiClient.saveClasses(updated);
    setNewClass({ name: '', image: '' });
  };

  const removeClass = (classIdentifier) => {
    const updatedClasses = classes.filter((cls) => {
      const cId = cls.id || cls._id;
      return cId !== classIdentifier && cls.name !== classIdentifier && cls._id !== classIdentifier;
    });
    setClasses(updatedClasses);
    apiClient.saveClasses(updatedClasses);
    apiClient.deleteClass(classIdentifier);
    if (
      selectedClass &&
      (selectedClass.id === classIdentifier ||
        selectedClass._id === classIdentifier ||
        selectedClass.name === classIdentifier)
    ) {
      setSelectedClass(null);
      localStorage.removeItem('selectedClass');
    }
  };

  // Hantera "Tillbaka"-knappen och rensa localStorage
  const handleBackButtonClick = () => {
    setSelectedClass(null);
    localStorage.removeItem('selectedClass'); // Rensa selectedClass från localStorage
  };

  return (
    <div className={styles.createClassesPageContainer}>
      {selectedClass ? (
        <div className={styles.classDetail}>
          <div className={styles.detailHeader}>
            <button onClick={handleBackButtonClick} className={styles.backButton}>
              ← {translate('back')}
            </button>
            <div className={styles.detailMeta}>
              <div>
                <p>Vald klass</p>
                <h3>{selectedClass.name}</h3>
              </div>
            </div>
          </div>

          <CompetitionGrid
            classId={selectedClass.id || selectedClass._id || selectedClass.name}
            setClassTotals={setClassTotals}
            competitions={competitions}
            canEdit={canEdit}
            onAddCompetition={(newComp) => {
              const updated = [...competitions, newComp];
              setCompetitions(updated);
              apiClient.saveCompetitions(updated);
            }}
          />
        </div>
      ) : (
        <>
          <section className={styles.formHeader}>
            <div>
              <p className={styles.formBadge}>Ny klass</p>
              <h1>{translate('Create Class')}</h1>
              <p className={styles.formIntro}>
                Fyll i detaljerna nedan för att skapa en klass och koppla den till tävlingarna.
              </p>
            </div>
          </section>

          {canEdit && (
            <section className={styles.formSection}>
              <div className={styles.formCard}>
                <h1>{translate('Create Class')}</h1>
                <p>Fyll i detaljerna nedan för att skapa en ny klass på några sekunder.</p>
                <form onSubmit={createNewClass} className={styles.classForm}>
                  <div>
                    <label>{translate('className')}</label>
                    <input
                      type="text"
                      placeholder={translate('className')}
                      value={newClass.name}
                      onChange={(e) =>
                        setNewClass((prev) => ({ ...prev, name: e.target.value }))
                      }
                      required
                    />
                  </div>

                  <div>
                    <label>{translate('imageUrl')}</label>
                    <div className={styles.imageInputContainer}>
                      <input
                        type="text"
                        placeholder="https://"
                        value={newClass.image}
                        onChange={(e) =>
                          setNewClass((prev) => ({ ...prev, image: e.target.value }))
                        }
                      />
                      <button type="button" className={styles.cameraButton} onClick={openFileDialog}>
                        <Camera size={20} />
                      </button>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>

                  <button type="submit" className={styles.primaryButton}>
                    {translate('createNew')}
                  </button>
                </form>
              </div>

              <div className={styles.previewCard}>
                <h3>Förhandsvisning</h3>
                <div className={styles.previewFrame}>
                  {newClass.image ? (
                    <img src={newClass.image} alt="Förhandsvisning" />
                  ) : (
                    <div className={styles.placeholderPreview}>
                      Ladda upp en bild eller klistra in en bild-URL för att se hur klassen kommer att presenteras.
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          <section className={styles.classGridSection}>
            <div className={styles.classGridHeader}>
              <h2>Alla klasser</h2>
              <span>{classes.length} registrerade</span>
            </div>

            {classes.length === 0 ? (
              <div className={styles.emptyState}>
                Inga klasser ännu. Börja med att lägga till din första klass via formuläret ovan.
              </div>
            ) : (
              <div className={styles.classGrid}>
                {classes.map((cls, idx) => {
                  const classIdentifier = cls.id || cls._id || cls.name || `cls-${idx}`;
                  return (
                    <ClassCard
                      key={classIdentifier}
                      classData={cls}
                      onClick={() => setSelectedClass(cls)}
                      onDelete={canEdit ? () => removeClass(classIdentifier) : undefined}
                      canEdit={canEdit}
                    />
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default withEditRights(CreateClassesPage);
